import React, { useState, useEffect } from "react";
import { useFileStore } from "../../store/fileStore";
import { api } from "../../utils/axios-instance";
import toast from "react-hot-toast";
import { storage } from "../../helper/firebaseClient";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import useAuth from "../../stores/authStore";
import FileView from "../../pages/FileViewPage";
import { useDepartment } from "../../stores/departmentStore";
import DeliverableListGrid from "./deliverables/DeliverableListGrid";
import ConfigureDeliverableModal from "./deliverables/ConfigureDeliverableModal";
import DeliverableChat from "../client/DeliverableChat";
import ResubmitModal from "../deliverables/ResubmitModal";
import { resolveDeliverableMimetype } from "./deliverables/utils";
import DeliverableFilterBar, {
  filterDeliverables,
} from "../deliverables/DeliverableFilterBar";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";

const hexToRgba = (hex, alpha) => {
  let r = 0,
    g = 0,
    b = 0;
  if (!hex) return `rgba(0,0,0,${alpha})`;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const normalizeFile = (file) => {
  if (!file) return {};
  const meta = file.metadata || {};

  const formatBytes = (bytes) => {
    if (bytes === null || bytes === undefined || Number.isNaN(Number(bytes)))
      return "";
    const b = Number(bytes);
    const units = ["B", "KB", "MB", "GB"];
    let i = 0;
    let n = b;
    while (n >= 1024 && i < units.length - 1) {
      n /= 1024;
      i += 1;
    }
    return `${n.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
  };

  return {
    id: file.id,
    download_url: file.download_url || file.downloadUrl || "",
    name: file.name || "",
    size: file.size ?? (meta.size !== undefined ? formatBytes(meta.size) : ""),
    mimetype: meta.mimetype || meta.mimeType || file.mimetype || "",
    color: file.color || "#5E5ADB",
    profilePic: file.profilePic || "",
    storage: file.storage || {},
    versioning: file.versioning || {},
    security: file.security || {},
  };
};

export default function DeliverablesTab({ roomId, theme }) {
  const profileData = useAuth((state) => state.profileData);
  const vendorName =
    profileData?.name ||
    (profileData?.first_name
      ? `${profileData.first_name} ${profileData.last_name || ""}`.trim()
      : "") ||
    profileData?.email ||
    "Vendor";
  const filteredData = useFileStore((state) => state.filteredData || []);
  const updateFilesState = useFileStore((state) => state.updateFilesState);
  const departments = useDepartment((state) => state.departments || []);
  const listDepartments = useDepartment((state) => state.listDepartments);
  const [deliverables, setDeliverables] = useState([]);
  const [loadingDeliverables, setLoadingDeliverables] = useState(false);
  const [sourceMode, setSourceMode] = useState(null); // 'local' | 'vault' | null
  const [paywallPrice, setPaywallPrice] = useState("");
  const [previewLimit, setPreviewLimit] = useState("");
  const [selectedVaultFile, setSelectedVaultFile] = useState(null);
  const [selectedLocalFile, setSelectedLocalFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [vaultSearch, setVaultSearch] = useState("");
  const [submittingDeliverable, setSubmittingDeliverable] = useState(false);
  const [customFileName, setCustomFileName] = useState("");
  const [editingDeliverable, setEditingDeliverable] = useState(null);
  const [editName, setEditName] = useState("");
  const [selectedFileForView, setSelectedFileForView] = useState(null);
  const [isFileViewOpen, setIsFileViewOpen] = useState(false);
  const [isFileCommentsOpen, setIsFileCommentsOpen] = useState(true);
  const [resubmitTargetDeliverable, setResubmitTargetDeliverable] =
    useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");

  const fetchDeliverables = async () => {
    try {
      setLoadingDeliverables(true);
      // Hit the explicit deliverables listing route for this specific room ID
      const res = await api.get(`/vendors/rooms/${roomId}/deliverables/`);
      setDeliverables(res.data?.deliverables || res.data || []);
    } catch (err) {
      console.error("Failed syncing deliverables roster matrix", err);
    } finally {
      setLoadingDeliverables(false);
    }
  };

  useEffect(() => {
    fetchDeliverables();
  }, [roomId]);

  useEffect(() => {
    if (sourceMode === "vault") {
      if (!departments || departments.length === 0) {
        listDepartments();
      }

      // Helper fallback for formatFileSize if needed
      const formatSize = (bytes) => {
        if (!bytes) return "";
        const units = ["B", "KB", "MB", "GB"];
        let size = Number(bytes);
        let i = 0;
        while (size >= 1024 && i < units.length - 1) {
          size /= 1024;
          i++;
        }
        return size.toFixed(2) + " " + units[i];
      };

      updateFilesState(0, formatSize, departments);
    }
  }, [sourceMode, departments, listDepartments, updateFilesState]);

  const formatFileSize = (bytes) => {
    if (bytes === null || bytes === undefined) return "";
    const units = ["B", "KB", "MB", "GB"];
    let size = Number(bytes);
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return size.toFixed(2) + " " + units[unitIndex];
  };

  const handleOpenEditModal = (item) => {
    setEditingDeliverable(item);
    setEditName(item.name || "");
    setPaywallPrice(item.paywallPrice ? String(item.paywallPrice) : "");
    setPreviewLimit(item.previewLimit ? String(item.previewLimit) : "");
  };

  const handleUpdateDeliverable = async (e) => {
    e.preventDefault();
    if (!editingDeliverable) return;
    const trimmedName = editName.trim();
    if (!trimmedName) {
      toast.error("Display name cannot be empty");
      return;
    }

    const targetId = editingDeliverable.id;
    const previous = editingDeliverable;
    const updates = {
      name: trimmedName,
      paywallPrice: Number(paywallPrice) || 0,
      previewLimit: parseInt(previewLimit, 10) || 0,
    };

    // Optimistic update — rolled back below if the request fails.
    setDeliverables((prev) =>
      prev.map((d) => (d.id === targetId ? { ...d, ...updates } : d))
    );
    setSubmittingDeliverable(true);
    try {
      const res = await api.patch(
        `/vendors/rooms/${roomId}/deliverables/${targetId}/update/`,
        updates
      );
      const saved = res.data?.deliverable;
      if (saved) {
        setDeliverables((prev) =>
          prev.map((d) => (d.id === targetId ? { ...d, ...saved } : d))
        );
      }
      toast.success("Deliverable updated successfully");
      resetModalState();
    } catch (err) {
      setDeliverables((prev) =>
        prev.map((d) => (d.id === targetId ? previous : d))
      );
      toast.error(err.response?.data?.error || "Failed to update deliverable");
    } finally {
      setSubmittingDeliverable(false);
    }
  };

  const handleDeleteDeliverable = async (item) => {
    if (
      !window.confirm(
        `Delete "${item.name}" from this room's deliverables? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      await api.delete(`/vendors/rooms/${roomId}/deliverables/${item.id}/`);
      toast.success("Deliverable removed from workspace pipeline");
      fetchDeliverables();
    } catch (err) {
      toast.error(
        err.response?.data?.error || "Failed to delete deliverable asset"
      );
    }
  };

  const handleOpenDeliverable = (item, resolvedMime) => {
    setSelectedFileForView({
      ...item,
      id: item.linkedOrgFileId || item.id,
      rawDeliverableId: item.id,
      name: item.name,
      download_url: item.download_url,
      mimetype: resolvedMime,
      currVersion: item.linkedOrgFileId || item.id,
      versioning: { versions: [] },
      metadata: { mimetype: resolvedMime },
    });
    setIsFileViewOpen(true);
  };

  const handleOpenResubmitModal = (item) => {
    setResubmitTargetDeliverable(item);
  };

  const handleCloseResubmitModal = () => {
    setResubmitTargetDeliverable(null);
  };

  const handleResubmitSuccess = () => {
    const resubmittedId = resubmitTargetDeliverable?.id;
    if (resubmittedId) {
      setDeliverables((prev) =>
        prev.map((d) =>
          d.id === resubmittedId ? { ...d, status: "pending" } : d
        )
      );
    }
    setResubmitTargetDeliverable(null);
    // Re-sync from the server so status, timestamps, and any message/comment
    // counters reflect the resubmission everywhere in the grid.
    fetchDeliverables();
  };

  const resetModalState = () => {
    setSourceMode(null);
    setSelectedLocalFile(null);
    setUploadProgress(0);
    setSelectedVaultFile(null);
    setVaultSearch("");
    setCustomFileName("");
    setPaywallPrice("");
    setPreviewLimit("");
    setEditingDeliverable(null);
    setEditName("");
  };

  const handleAddDeliverable = async (e) => {
    e.preventDefault();
    if (sourceMode === "local" && !selectedLocalFile) {
      toast.error("Please pick a file to upload first");
      return;
    }
    if (sourceMode === "vault" && !selectedVaultFile) {
      toast.error("Please pick an asset from your organization vault list");
      return;
    }

    setSubmittingDeliverable(true);
    const orgId = profileData?.org || profileData?.org_id || "";

    let payload = {
      roomId: roomId,
      sourceMode: sourceMode,
      paywallPrice: paywallPrice || 0,
      previewLimit: previewLimit || 0,
    };

    try {
      if (sourceMode === "local") {
        const fileId = Math.random().toString(36).substring(2, 15);
        const isolatedPath = `deliverables/${orgId}/${roomId}/${fileId}_${selectedLocalFile.name}`;
        const fileRef = ref(storage, isolatedPath);

        const customMeta = {
          customMetadata: {
            org_id: orgId,
            room_id: roomId,
          },
        };

        // Trigger resumable upload monitor loops
        const uploadTask = uploadBytesResumable(
          fileRef,
          selectedLocalFile,
          customMeta
        );

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (err) => reject(err),
            async () => {
              const resolvedUrl = await getDownloadURL(uploadTask.snapshot.ref);
              payload = {
                ...payload,
                name: customFileName.trim()
                  ? customFileName.trim() +
                    (selectedLocalFile.name.includes(".")
                      ? selectedLocalFile.name.substring(
                          selectedLocalFile.name.lastIndexOf(".")
                        )
                      : "")
                  : selectedLocalFile.name,
                mimetype:
                  selectedLocalFile.type ||
                  resolveDeliverableMimetype(selectedLocalFile),
                size: formatFileSize(selectedLocalFile.size),
                download_url: resolvedUrl,
                gcsObjectPath: isolatedPath,
              };
              resolve();
            }
          );
        });
      } else if (sourceMode === "vault") {
        payload = {
          ...payload,
          fileId: selectedVaultFile.id,
          name: selectedVaultFile.name,
          mimetype: selectedVaultFile.mimetype || "",
          size: selectedVaultFile.size || "",
          download_url: selectedVaultFile.download_url,
          gcsObjectPath: selectedVaultFile.storage?.object || "",
          color: selectedVaultFile.color || "#c8c7d5",
        };
      }

      await api.post("/vendors/rooms/upload-deliverable/", payload);
      toast.success("Deliverable linked into workspace pipeline successfully");

      resetModalState();
      fetchDeliverables();
    } catch (err) {
      toast.error(
        err.response?.data?.error || "Failed adding deliverable asset entry"
      );
    } finally {
      setSubmittingDeliverable(false);
    }
  };

  const normalizedVaultFiles = filteredData.map(normalizeFile);
  const visibleDeliverables = filterDeliverables(
    deliverables,
    selectedStatus,
    searchQuery
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* ACTION TOOLBAR */}
      <div style={{ display: "flex", gap: "12px" }}>
        <button
          onClick={() => setSourceMode("local")}
          style={{
            background: "#5E5ADB",
            color: "#fff",
            border: "none",
            padding: "10px 16px",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          + Upload from Device
        </button>
        <button
          onClick={() => setSourceMode("vault")}
          style={{
            background: "transparent",
            color: "#5E5ADB",
            border: `1px solid #5E5ADB`,
            padding: "10px 16px",
            borderRadius: "6px",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          + Select from Org Vault
        </button>
      </div>

      {deliverables.length > 0 && (
        <DeliverableFilterBar
          deliverables={deliverables}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          theme={theme}
        />
      )}

      {/* DELIVERABLES ASSET DISPLAY LIST GRID */}
      <DeliverableListGrid
        deliverables={visibleDeliverables}
        emptyMessage={
          deliverables.length > 0
            ? "No deliverables match your search or filter."
            : undefined
        }
        loadingDeliverables={loadingDeliverables}
        theme={theme}
        onEdit={handleOpenEditModal}
        onDelete={handleDeleteDeliverable}
        onOpen={handleOpenDeliverable}
        onResubmit={handleOpenResubmitModal}
      />

      {/* PIPELINE CONFIGURATION MODAL */}
      {(sourceMode || editingDeliverable) && (
        <ConfigureDeliverableModal
          theme={theme}
          sourceMode={sourceMode}
          isEditMode={!!editingDeliverable}
          editName={editName}
          setEditName={setEditName}
          onSubmit={
            editingDeliverable ? handleUpdateDeliverable : handleAddDeliverable
          }
          onCancel={resetModalState}
          selectedLocalFile={selectedLocalFile}
          setSelectedLocalFile={setSelectedLocalFile}
          customFileName={customFileName}
          setCustomFileName={setCustomFileName}
          uploadProgress={uploadProgress}
          formatFileSize={formatFileSize}
          vaultSearch={vaultSearch}
          setVaultSearch={setVaultSearch}
          normalizedVaultFiles={normalizedVaultFiles}
          selectedVaultFile={selectedVaultFile}
          setSelectedVaultFile={setSelectedVaultFile}
          hexToRgba={hexToRgba}
          paywallPrice={paywallPrice}
          setPaywallPrice={setPaywallPrice}
          previewLimit={previewLimit}
          setPreviewLimit={setPreviewLimit}
          submittingDeliverable={submittingDeliverable}
        />
      )}

      <ResubmitModal
        deliverable={resubmitTargetDeliverable}
        isOpen={!!resubmitTargetDeliverable}
        onClose={handleCloseResubmitModal}
        onSuccess={handleResubmitSuccess}
        roomId={roomId}
      />

      {isFileViewOpen && selectedFileForView && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 1200,
            overflowY: "auto",
          }}
        >
          <div
            style={{
              background: theme?.bg || "#f9fafb",
              minHeight: "100%",
              height: "100%",
              padding: "32px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "16px",
              }}
            >
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  color: theme?.textMain || "#111827",
                  margin: 0,
                }}
              >
                {selectedFileForView?.name}
              </h2>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <button
                  type="button"
                  aria-label="Toggle comments"
                  onClick={() => setIsFileCommentsOpen((prev) => !prev)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    background: isFileCommentsOpen
                      ? theme?.cardBg || "#ffffff"
                      : "transparent",
                    border: `1px solid ${theme?.border || "#e5e7eb"}`,
                    color: theme?.textMain || "#111827",
                    borderRadius: "6px",
                    padding: "0 12px",
                    height: "32px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  <ChatBubbleOutlineIcon style={{ fontSize: "16px" }} />
                  Comments
                </button>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => {
                    setIsFileViewOpen(false);
                    setSelectedFileForView(null);
                  }}
                  style={{
                    background: "transparent",
                    border: `1px solid ${theme?.border || "#e5e7eb"}`,
                    color: theme?.textMain || "#111827",
                    borderRadius: "6px",
                    width: "32px",
                    height: "32px",
                    fontSize: "16px",
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "row",
                width: "100%",
                height: "calc(100vh - 125px)",
                gap: "16px",
                flex: 1,
                minHeight: 0,
              }}
            >
              <div
                style={{
                  width: isFileCommentsOpen ? "70%" : "100%",
                  height: "100%",
                  overflow: "hidden",
                  transition: "width 0.2s ease-in-out",
                }}
              >
                <FileView
                  fileInfo={selectedFileForView}
                  closeDrawer={() => {
                    setIsFileViewOpen(false);
                    setSelectedFileForView(null);
                  }}
                  sharedFileInfo={{}}
                  embedded={true}
                />
              </div>

              {isFileCommentsOpen && (
                <div
                  style={{
                    width: "30%",
                    height: "100%",
                    padding: "16px",
                    borderLeft: `1px solid ${theme?.border || "#e5e7eb"}`,
                    background: theme?.cardBg || "#ffffff",
                    borderRadius: "8px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "15px",
                      fontWeight: "700",
                      color: "#111827",
                      margin: "0 0 12px 0",
                    }}
                  >
                    Client Feedback
                  </h3>
                  <div style={{ flex: 1, minHeight: 0 }}>
                    <DeliverableChat
                      orgId={profileData?.org || profileData?.org_id}
                      roomId={roomId}
                      deliverableId={
                        selectedFileForView?.rawDeliverableId ||
                        selectedFileForView?.id
                      }
                      userType="vendor"
                      userName={vendorName}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
